import { memo, useCallback, useEffect } from 'react';

import { useAppStateDispatch, useAppStateSelector } from '@src/providers/AppStateProvider';
import type { FormRow } from '@talos/kyoko';
import {
  Button,
  ButtonVariants,
  Flex,
  FormTable,
  GenerateCustomerApiKeyDialog,
  IconName,
  NotificationVariants,
  PanelActions,
  VStack,
  copyText,
  useConstant,
  useDisclosure,
  useFormTable,
  useGlobalToasts,
  type Column,
  type CustomerUserApiKey,
} from '@talos/kyoko';
import type { IRowNode } from 'ag-grid-community';
import { deleteApiKey, updateApiKey } from '../state/apiKeys/apiKeysSlice';
import { useCustomerApiKeys } from './useCustomerApiKeys';

const getApiSecret = (data: CustomerUserApiKey) => {
  return data.ApiSecret || '*****';
};

export const CustomerApiKeysFormTable = memo(() => {
  const { add: addToast } = useGlobalToasts();
  const { apiKeys, dirtyApiKeyRowIds, deletedApiKeys, createAPIKey, fetchApiKeys } = useCustomerApiKeys();
  const dispatch = useAppStateDispatch();
  const customerUserId = useAppStateSelector(state => state.customerUserDrawer.userDetails.customerUserId);
  const generateDialog = useDisclosure();

  const columns = useConstant<Column[]>([
    { type: 'date', field: 'Timestamp', width: 140, sort: '-' },
    { type: 'text', field: 'Label', title: 'Label', editable: true, width: 110 },
    { type: 'text', field: 'ApiKey', title: 'API Key', width: 120 },
    {
      type: 'custom',
      field: 'ApiSecret',
      title: 'API Secret',
      width: 160,
      params: {
        valueGetter: ({ data }: { data: CustomerUserApiKey }) => getApiSecret(data),
      },
    },
    { type: 'text', field: 'WhitelistedIps', title: 'Whitelisted IPs', editable: true, width: 130 },
    { type: 'filler', id: 'filler' },
    {
      type: 'iconButton',
      id: 'copy',
      pinned: 'right',
      params: {
        icon: IconName.ClipboardCopy,
        onClick: ({ node }: { node: IRowNode }) => {
          const rowData: CustomerUserApiKey = node.data;
          copyText(`USER:${rowData.CustomerUserID}
KEY:${rowData.ApiKey}
SECRET:${getApiSecret(rowData)}`);
          addToast({
            text: `Details copied to clipboard.`,
            variant: NotificationVariants.Default,
          });
        },
      },
      width: 40,
    },
    {
      type: 'remove',
      id: 'remove',
      pinned: 'right',
      params: {
        onClick: ({ node }: { node: IRowNode<CustomerUserApiKey> }) => {
          node.data && dispatch(deleteApiKey(node.data));
        },
      },
    },
  ]);

  const handleRowChange = useCallback(
    (row: FormRow<CustomerUserApiKey>) => {
      dispatch(updateApiKey(row.data));
    },
    [dispatch]
  );

  const formTable = useFormTable<CustomerUserApiKey>({
    rowID: 'CustomerUserApiKeyID',
    data: apiKeys,
    dirtyRowIds: dirtyApiKeyRowIds,
    deletedRowIds: deletedApiKeys,
    columns,
    onChange: handleRowChange,
  });

  const { reset } = formTable;
  useEffect(() => {
    reset(apiKeys, dirtyApiKeyRowIds, deletedApiKeys);
  }, [reset, apiKeys, dirtyApiKeyRowIds, deletedApiKeys]);

  useEffect(() => {
    if (customerUserId) {
      fetchApiKeys(customerUserId);
    }
  }, [customerUserId, fetchApiKeys]);

  const headerActions = (
    <PanelActions>
      <Button startIcon={IconName.Plus} variant={ButtonVariants.Positive} onClick={() => generateDialog.open()}>
        Generate API Key
      </Button>
    </PanelActions>
  );

  return (
    <VStack alignItems="stretch" h="100%">
      <GenerateCustomerApiKeyDialog
        {...generateDialog}
        usePortal={false}
        title="Generate API Key"
        label="Label"
        confirmLabel="Generate API Key"
        onGenerate={createAPIKey}
      />
      <Flex justifyContent="flex-end" mt="spacing16" mb="spacing16" alignItems="baseline">
        {headerActions}
      </Flex>
      <FormTable {...formTable} />
    </VStack>
  );
});
